/**
 * Skeleton — 목록·카드 로딩 자리표시 (반짝이는 회색 막대)
 * ----------------------------------------------------------------------------
 * "불러오는 중…" 글자 대신, 곧 들어올 줄의 모양을 미리 그린다.
 *  - Skeleton: 막대 하나. width/height 는 숫자(px) 또는 CSS 길이("60%").
 *  - SkeletonRows: 목록 행 자리표시 — [아이콘 칸] + 줄 두 개(제목·부제), cols 를 주면 표처럼 칸을 나눈다.
 *
 * ★ 반짝임은 1.4s 왼→오 그라데이션(목록 로딩 동안만 — 데이터가 오면 사라진다).
 *   prefers-reduced-motion 이면 반짝임 없이 고정 회색.
 * ★ ds-scope 안·밖 어디서나 같은 모양 — 크기·모양을 인라인/토큰 클래스로 직접 적고 리셋에 기대지 않는다.
 *   키프레임은 전역 CSS 파일을 건드리지 않게 처음 쓸 때 <head> 에 한 번만 넣는다.
 *
 * 사용 예:
 *   {loading ? <SkeletonRows rows={5} /> : <List … />}
 *   <Skeleton width={120} height={14} />
 */
import React from "react";

const STYLE_ID = "vault-skeleton-style";
const CSS = `
@keyframes vault-skeleton-shimmer { from { background-position: 100% 0; } to { background-position: -100% 0; } }
.vault-skeleton {
  display: block; box-sizing: border-box;
  background-color: #edf1f8;
  background-image: linear-gradient(90deg, #edf1f8 0%, #f5f7fb 40%, #edf1f8 80%);
  background-size: 200% 100%;
  animation: vault-skeleton-shimmer 1.4s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .vault-skeleton { animation: none; background-image: none; }
}
`;

/** 키프레임을 <head> 에 한 번만 넣는다 */
function useSkeletonStyle() {
  React.useEffect(() => {
    if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
}

const len = (v: number | string | undefined) => (typeof v === "number" ? `${v}px` : v);

export type SkeletonProps = {
  /** 숫자=px · 문자열=CSS 길이. 기본 100% */
  width?: number | string;
  /** 기본 12px(본문 글자 높이) */
  height?: number | string;
  /** 모서리 — 기본 rounded(4px). 원형은 "rounded-pill" 을 넘긴다 */
  className?: string;
  style?: React.CSSProperties;
};

export function Skeleton({ width = "100%", height = 12, className = "rounded", style }: SkeletonProps) {
  useSkeletonStyle();
  return <span aria-hidden="true" className={`vault-skeleton ${className}`} style={{ width: len(width), height: len(height), ...style }} />;
}

/** 줄마다 폭을 조금씩 달리해 "진짜 글자" 처럼 보이게 */
const TITLE_W = ["62%", "48%", "70%", "55%", "40%", "66%"];
const SUB_W = ["38%", "30%", "44%", "26%", "34%", "41%"];

export type SkeletonRowsProps = {
  /** 행 수 — 기본 5 */
  rows?: number;
  /** 표처럼 칸을 나눌 때 칸 수. 없으면 [아이콘 + 제목·부제 두 줄] 목록 행 */
  cols?: number;
  /** 행 사이 구분선 — 기본 true */
  divided?: boolean;
  className?: string;
  /** 스크린리더에 읽힐 문구 — 기본 "불러오는 중" */
  label?: string;
};

export function SkeletonRows({ rows = 5, cols, divided = true, className = "", label = "불러오는 중" }: SkeletonRowsProps) {
  useSkeletonStyle();
  return (
    <div role="status" aria-live="polite" aria-label={label} className={className} style={{ display: "grid" }}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className={divided && i > 0 ? "border-0 border-t border-solid border-gray-200" : undefined}
          style={{ boxSizing: "border-box", padding: "10px 12px", minHeight: 44 }}
        >
          {cols && cols > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12, alignItems: "center" }}>
              {Array.from({ length: cols }, (_, c) => (
                <Skeleton key={c} height={12} width={c === 0 ? TITLE_W[i % TITLE_W.length] : SUB_W[(i + c) % SUB_W.length]} />
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "28px minmax(0, 1fr)", gap: 10, alignItems: "center" }}>
              <Skeleton width={28} height={28} className="rounded-card-sm" />
              <div style={{ display: "grid", gap: 6 }}>
                <Skeleton height={12} width={TITLE_W[i % TITLE_W.length]} />
                <Skeleton height={10} width={SUB_W[i % SUB_W.length]} />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
